import React, { useState, useEffect } from 'react';
import { X, Play, AlertCircle, CheckCircle, ArrowRight, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { unitCodeService } from '../../services/unitCodeService';

interface UnitCodePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const UnitCodePopup: React.FC<UnitCodePopupProps> = ({ isOpen, onClose, onSuccess }) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showNavigationOptions, setShowNavigationOptions] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isOnMyLecturesPage = window.location.pathname === '/my-lectures'
    || window.location.pathname === '/student-portal'
    || window.location.pathname === '/student-portal/my-lessons';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check authentication first
    if (!isAuthenticated) {
      navigate('/login', { state: { returnTo: '/' } });
      return;
    }
    
    if (!code.trim()) {
      setError('يرجى إدخال كود المحاضرة');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await unitCodeService.enterUnitCode(code.trim());
      
      if (response.success) {
        setSuccess(true);
        setCode('');
        
        // If onSuccess callback is provided (e.g., from MyLectures page), don't show navigation options
        if (onSuccess) {
          onSuccess();
          // Close popup after a short delay to show success message
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          // Show navigation options if not on MyLectures page
          setShowNavigationOptions(true);
        }
      } else {
        setError(response.message || 'كود غير صحيح. يرجى المحاولة مرة أخرى.');
      }
    } catch (err: any) {
      const errorMessage = unitCodeService.formatErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCode('');
    setError('');
    setSuccess(false);
    setShowNavigationOptions(false);
    setIsLoading(false);
    onClose();
  };

  const handleGoToMyLectures = () => {
    navigate('/student-portal');
    handleClose();
  };

  const handleGoToHome = () => {
    navigate('/');
    handleClose();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 scale-100">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-2xl">
          <button
            onClick={handleClose}
            className="absolute top-4 left-4 p-2 hover:bg-white/20 rounded-full transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">دخول المحاضرة</h2>
            <p className="text-blue-100">أدخل كود المحاضرة للوصول للمحتوى</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Code Input */}
            <div>
              <label htmlFor="unitCode" className="block text-sm font-medium text-gray-700 mb-2">
                كود المحاضرة
              </label>
              <input
                id="unitCode"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="مثال: ABC123"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-center text-lg font-mono tracking-wider"
                disabled={isLoading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Success Message with Navigation Options */}
            {success && !showNavigationOptions && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">تم التحقق من الكود بنجاح!</span>
              </div>
            )}

            {/* Navigation Options */}
            {showNavigationOptions && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-center">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">تم التحقق من الكود بنجاح!</p>
                    <p className="text-sm text-green-600">اختر وجهتك التالية</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  {/* Go to My Lectures */}
                  <button
                    onClick={handleGoToMyLectures}
                    className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    <div className="flex items-center gap-3">
                      <Play className="w-5 h-5" />
                      <span className="font-semibold">انتقل للمحاضرات</span>
                    </div>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  
                  {/* Go to Home */}
                  <button
                    onClick={handleGoToHome}
                    className="flex items-center justify-between p-4 bg-white border-2 border-gray-200 hover:border-blue-300 text-gray-700 hover:text-blue-600 rounded-xl transition-all duration-300 hover:bg-blue-50"
                  >
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5" />
                      <span className="font-semibold">العودة للرئيسية</span>
                    </div>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Submit Button - Only show if not showing navigation options */}
            {!showNavigationOptions && (
              <button
                type="submit"
                disabled={isLoading || !code.trim()}
                className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 transform ${
                  isLoading || !code.trim()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white shadow-lg hover:shadow-xl hover:scale-105'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>جاري التحقق...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Play className="w-5 h-5" />
                    <span>دخول المحاضرة</span>
                  </div>
                )}
              </button>
            )}
          </form>

          {/* Help text */}
          {!showNavigationOptions && (
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>يمكنك الحصول على كود المحاضرة من المدرس</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitCodePopup;
